/**
 * KAN-678: окно отправки Telegram-рассылки на странице кампаний.
 *
 * Чистые функции без React: чтение окна из values канала `telegram_user`
 * (и окна обзвона из канала `voice`), подпись строки, валидация формы и
 * сборка тела PUT. Компонент и хук живут в components/campaigns и hooks.
 *
 * Сохранение идёт через PUT /users/{id}/channels/telegram_user/, который
 * ПОЛНОСТЬЮ заменяет не-секретные values (merge_channel_values на бэкенде
 * берёт только присланные ключи; секреты и служебные ключи аккаунтов он
 * сохраняет сам). Поэтому в тело уходят все текущие values без масок
 * секретов плюс изменённые ключи окна.
 */
import { zoneLabel } from '@/lib/outreachTimeline';
import {
  DEFAULT_WEEKDAYS,
  WEEKDAYS_REQUIRED_ERROR,
  formatWeekdays,
  normalizeWeekdays,
} from '@/lib/weekdays';
import type { ChannelField, UserChannel } from '@/types/user';

export const TELEGRAM_USER_CODENAME = 'telegram_user';
export const VOICE_CODENAME = 'voice';

/** Типы полей схемы, значения которых API отдаёт маской и не принимает назад. */
const SECRET_TYPES = new Set(['password', 'secret']);

const DEFAULTS = {
  window_start: '10:00',
  window_end: '19:00',
  delay_min_s: 180,
  delay_max_s: 480,
  daily_new_chats_limit: 20,
} as const;

const LIMIT_BOUNDS = { min: 1, max: 200 } as const;

export interface OutreachWindowValues {
  window_start: string;
  window_end: string;
  delay_min_s: number;
  delay_max_s: number;
  daily_new_chats_limit: number;
  /** KAN-764: дни рассылки, ISO 1=пн..7=вс, отсортированы. */
  send_days: number[];
}

export interface VoiceWindowValues {
  call_window_start: string;
  call_window_end: string;
  calls_per_hour: number;
}

/** Поля формы диалога (строки, как в инпутах). */
export interface OutreachWindowForm {
  window_start: string;
  window_end: string;
  delay_min_s: string;
  delay_max_s: string;
  daily_new_chats_limit: string;
  send_days: number[];
}

export interface NumberBounds {
  min: number;
  max: number;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function schemaDefault(channel: UserChannel | undefined, name: string): unknown {
  return channel?.config_schema?.find((f) => f.name === name)?.default;
}

function readTime(channel: UserChannel | undefined, name: string, fallback: string): string {
  const raw = channel?.values?.[name];
  if (typeof raw === 'string' && HHMM.test(raw.trim())) return raw.trim();
  const def = schemaDefault(channel, name);
  return typeof def === 'string' && HHMM.test(def) ? def : fallback;
}

function readInt(channel: UserChannel | undefined, name: string, fallback: number): number {
  const raw = channel?.values?.[name];
  const num = typeof raw === 'number' ? raw : Number(raw);
  if (raw !== '' && raw != null && Number.isFinite(num)) return Math.trunc(num);
  const def = schemaDefault(channel, name);
  return typeof def === 'number' ? def : fallback;
}

function readDays(channel: UserChannel | undefined, name: string): number[] {
  const stored = normalizeWeekdays(channel?.values?.[name]);
  if (stored && stored.length > 0) return stored;
  const def = normalizeWeekdays(schemaDefault(channel, name));
  return def && def.length > 0 ? def : [...DEFAULT_WEEKDAYS];
}

/** Канал по codename из карточки пользователя. */
export function findChannel(
  channels: UserChannel[] | undefined,
  codename: string
): UserChannel | undefined {
  return channels?.find((c) => c.codename === codename);
}

/** Окно отправки из values канала telegram_user; пусто или мусор - умолчания схемы. */
export function readOutreachWindow(channel: UserChannel | undefined): OutreachWindowValues {
  return {
    window_start: readTime(channel, 'window_start', DEFAULTS.window_start),
    window_end: readTime(channel, 'window_end', DEFAULTS.window_end),
    delay_min_s: readInt(channel, 'delay_min_s', DEFAULTS.delay_min_s),
    delay_max_s: readInt(channel, 'delay_max_s', DEFAULTS.delay_max_s),
    daily_new_chats_limit: readInt(channel, 'daily_new_chats_limit', DEFAULTS.daily_new_chats_limit),
    send_days: readDays(channel, 'send_days'),
  };
}

/** Окно обзвона из values канала voice; нет канала - null (строка не показывается). */
export function readVoiceWindow(channel: UserChannel | undefined): VoiceWindowValues | null {
  if (!channel) return null;
  return {
    call_window_start: readTime(channel, 'call_window_start', '09:00'),
    call_window_end: readTime(channel, 'call_window_end', '21:00'),
    calls_per_hour: readInt(channel, 'calls_per_hour', 30),
  };
}

/** Границы лимита новых чатов из схемы канала (min/max поля), иначе 1..200. */
export function limitBounds(channel: UserChannel | undefined): NumberBounds {
  const field: ChannelField | undefined = channel?.config_schema?.find(
    (f) => f.name === 'daily_new_chats_limit'
  );
  return {
    min: typeof field?.min === 'number' ? field.min : LIMIT_BOUNDS.min,
    max: typeof field?.max === 'number' ? field.max : LIMIT_BOUNDS.max,
  };
}

/** Русское склонение: 1 новый чат, 2 новых чата, 5 новых чатов. */
export function chatsLabel(count: number): string {
  const n = Math.abs(count) % 100;
  const last = n % 10;
  if (n > 10 && n < 20) return `${count} новых чатов`;
  if (last === 1) return `${count} новый чат`;
  if (last >= 2 && last <= 4) return `${count} новых чата`;
  return `${count} новых чатов`;
}

/** "пн-пт 10:00-19:00 МСК, пауза 60-120 с, лимит 5 новых чатов в день". */
export function formatOutreachWindow(values: OutreachWindowValues, timezone: string): string {
  const days = formatWeekdays(values.send_days);
  return (
    `${days ? `${days} ` : ''}${values.window_start}-${values.window_end} ${zoneLabel(timezone)}, ` +
    `пауза ${values.delay_min_s}-${values.delay_max_s} с, ` +
    `лимит ${chatsLabel(values.daily_new_chats_limit)} в день`
  );
}

/** "09:00-21:00 МСК, до 30 звонков в час". */
export function formatVoiceWindow(values: VoiceWindowValues, timezone: string): string {
  return (
    `${values.call_window_start}-${values.call_window_end} ${zoneLabel(timezone)}, ` +
    `до ${values.calls_per_hour} звонков в час`
  );
}

export function formFromValues(values: OutreachWindowValues): OutreachWindowForm {
  return {
    window_start: values.window_start,
    window_end: values.window_end,
    delay_min_s: String(values.delay_min_s),
    delay_max_s: String(values.delay_max_s),
    daily_new_chats_limit: String(values.daily_new_chats_limit),
    send_days: [...values.send_days],
  };
}

function parseInt10(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^-?\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/**
 * Проверка формы. Возвращает текст ошибки или null.
 *
 * Конец окна позже начала (ночное окно через полночь здесь не поддерживается:
 * планировщик рассылки работает по дневному окну), пауза min <= max и обе
 * неотрицательны, лимит в границах схемы.
 */
export function validateOutreachWindowForm(
  form: OutreachWindowForm,
  bounds: NumberBounds
): string | null {
  if (!HHMM.test(form.window_start) || !HHMM.test(form.window_end)) {
    return 'Укажите начало и конец окна в формате ЧЧ:ММ';
  }
  if (form.window_end <= form.window_start) {
    return 'Конец окна должен быть позже начала';
  }
  if ((normalizeWeekdays(form.send_days) ?? []).length === 0) {
    return WEEKDAYS_REQUIRED_ERROR;
  }
  const min = parseInt10(form.delay_min_s);
  const max = parseInt10(form.delay_max_s);
  if (min === null || max === null || min < 0 || max < 0) {
    return 'Пауза между сообщениями: целое число секунд, не меньше 0';
  }
  if (min > max) {
    return 'Минимальная пауза не может быть больше максимальной';
  }
  const limit = parseInt10(form.daily_new_chats_limit);
  if (limit === null || limit < bounds.min || limit > bounds.max) {
    return `Лимит новых чатов в день: целое число от ${bounds.min} до ${bounds.max}`;
  }
  return null;
}

/** Значения из проверенной формы (числа уже целые). */
export function valuesFromForm(form: OutreachWindowForm): OutreachWindowValues {
  return {
    window_start: form.window_start,
    window_end: form.window_end,
    delay_min_s: Number(form.delay_min_s.trim()),
    delay_max_s: Number(form.delay_max_s.trim()),
    daily_new_chats_limit: Number(form.daily_new_chats_limit.trim()),
    send_days: normalizeWeekdays(form.send_days) ?? [],
  };
}

/**
 * Тело `values` для PUT канала: все текущие values без масок секретов
 * (`***` и флаги `<name>_is_set`) плюс ключи окна. Остальные настройки канала
 * (шаблоны, стоп-слова, профиль клиента и т.д.) уходят как были, иначе PUT
 * их затёр бы.
 */
export function buildSaveValues(
  channel: UserChannel,
  patch: OutreachWindowValues
): Record<string, unknown> {
  const secretNames = new Set(
    (channel.config_schema ?? []).filter((f) => SECRET_TYPES.has(f.type)).map((f) => f.name)
  );
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(channel.values ?? {})) {
    if (secretNames.has(key)) continue;
    if (key.endsWith('_is_set') && secretNames.has(key.slice(0, -'_is_set'.length))) continue;
    result[key] = value;
  }
  return { ...result, ...patch };
}
